import { nextTick, ref } from "vue";

const SCROLL_THRESHOLD = 100; // Distance from edge to start scrolling
const SCROLL_SPEED = 10; // Pixels to scroll per interval
const SCROLL_INTERVAL_MS = 16; // ~60fps

/**
 * @description Composable for auto-scrolling during drag operations
 * @param containerId - ID of the scrollable container element (default: "form-builder")
 * @returns Object with handleDragStart and handleDragEnd functions to attach to VueDraggableNext
 */
export function useAutoScroll(containerId = "form-builder") {
  const isDragging = ref(false);
  const scrollInterval = ref<number | null>(null);

  /**
   * @description Clears the scroll interval if it exists
   */
  const clearScrollInterval = () => {
    if (scrollInterval.value !== null) {
      clearInterval(scrollInterval.value);
      scrollInterval.value = null;
    }
  };

  /**
   * @description Creates a scroll interval for the specified direction
   * @param direction - "up" or "down"
   */
  const createScrollInterval = (direction: "up" | "down") => {
    if (scrollInterval.value) {
      return; // Already scrolling
    }

    scrollInterval.value = window.setInterval(() => {
      const container = document.getElementById(containerId);
      if (!container || !isDragging.value) {
        clearScrollInterval();
        return;
      }

      const maxScroll = container.scrollHeight - container.clientHeight;

      if (direction === "up") {
        if (container.scrollTop > 0) {
          container.scrollTop = Math.max(0, container.scrollTop - SCROLL_SPEED);
        } else {
          clearScrollInterval();
        }
      } else {
        // direction === "down"
        if (container.scrollTop < maxScroll) {
          container.scrollTop = Math.min(maxScroll, container.scrollTop + SCROLL_SPEED);
        } else {
          clearScrollInterval();
        }
      }
    }, SCROLL_INTERVAL_MS);
  };

  const startAutoScroll = () => {
    const scrollContainer = document.getElementById(containerId);
    if (!scrollContainer) {
      return;
    }

    let dragHandler: ((e: DragEvent) => void) | null = null;

    // Use 'drag' event which fires continuously during drag and doesn't interfere with drops
    dragHandler = (e: DragEvent) => {
      if (!isDragging.value || !scrollContainer) {
        clearScrollInterval();
        return;
      }

      const mouseY = e.clientY;
      const viewportHeight = window.innerHeight;

      // Check if mouse is near top edge of viewport
      if (mouseY < SCROLL_THRESHOLD) {
        createScrollInterval("up");
      }
      // Check if mouse is near bottom edge of viewport
      else if (mouseY > viewportHeight - SCROLL_THRESHOLD) {
        createScrollInterval("down");
      } else {
        // Not near edges, stop scrolling
        clearScrollInterval();
      }
    };

    document.addEventListener("drag", dragHandler, { passive: true });
    // Store handler for cleanup
    (scrollContainer as any)._autoScrollDragHandler = dragHandler;
  };

  const stopAutoScroll = () => {
    clearScrollInterval();
    const scrollContainer = document.getElementById(containerId);
    if (scrollContainer) {
      if ((scrollContainer as any)._autoScrollDragHandler) {
        document.removeEventListener("drag", (scrollContainer as any)._autoScrollDragHandler);
        delete (scrollContainer as any)._autoScrollDragHandler;
      }
    }
  };

  const handleDragStart = () => {
    isDragging.value = true;
    nextTick(() => {
      startAutoScroll();
    });
  };

  const handleDragEnd = () => {
    isDragging.value = false;
    stopAutoScroll();
  };

  return {
    handleDragStart,
    handleDragEnd,
  };
}
